<template>
  <div class="known-business-entity-contact-combobox w-full">
    <KnownBusinessEntityContactCardWrapper
      :known-business-entity-contact="selectedKnownBusinessEntityContact"
      :show-verification-warning="false"
      :disabled="hasOptionsOpen"
    >
      <InputCombobox
        v-model="selectedKnownBusinessEntityContact"
        @update:model-value="modelValue = $event"
        v-bind="inheritedAttrs"
        items-key-name="id"
        :items="filteredAllowedItems"
        :items-value-function="(kbec: KnownBusinessEntityContact) => kbec.getDisplayText()"
        :items-filter-fn="() => true"
        :sort-alphabetically="false"
        creation-option
        :creation-model-type="Person"
        :edit-model-conversion-function="(kbec: KnownBusinessEntityContact) => kbec.person"
        :selected-item-display-fn="getSelectedItemDisplay"
        :auto-select-newly-created-model="false"
        @query-changed="comboboxText = $event"
        @item-created="onItemCreated"
        @item-selected="displayKnownBusinessEntity = false"
        @is-creating-new-item="$emit('isCreatingNewItem', $event)"
        @combobox-options-open-change="hasOptionsOpen = $event"
        ref="combobox"
      >
        <template v-if="$slots['before-input']" #before-input>
          <slot name="before-input" />
        </template>
        <template v-if="$slots['custom-selected-item']" #custom-selected-item="{ item }">
          <slot name="custom-selected-item" v-bind="{ item }" />
        </template>
        <template v-if="$slots['custom-list-item']" #custom-list-item="{ item, active, disabled }">
          <slot name="custom-list-item" v-bind="{ item, active, disabled }" />
        </template>
        <template #custom-options="{ disabled, onMouseDownLeft, onMouseUpLeft }">
          <CustomKnownBusinessEntityContactComboboxOptions
            :allowed-roles="allowedRoles"
            :combobox-text="comboboxText"
            :save-handle="onItemSaved"
            :close-handle="() => $emit('isCreatingNewItem', false)"
            :is-creating-new-item-handle="() => $emit('isCreatingNewItem', true)"
            :disabled="disabled"
            @option-mousedown.left="onMouseDownLeft"
            @option-mouseup.left="onMouseUpLeft"
          />
        </template>
      </InputCombobox>
    </KnownBusinessEntityContactCardWrapper>
  </div>
</template>

<script setup lang="ts">
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import KnownBusinessEntityContactCardWrapper from "@/components/known_business_entity_contact/KnownBusinessEntityContactCardWrapper.vue"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { Person } from "@/models/person.model"
import { storeToRefs } from "pinia"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store.ts"
import { computed, ref, useAttrs, watch, type Ref } from "vue"
import CustomKnownBusinessEntityContactComboboxOptions from "@/components/inputs/input_combobox/CustomKnownBusinessEntityContactComboboxOptions.vue"
import { RoleTypes } from "@/enums/role-types"
import { fuzzySearchCollection } from "@/helper/collection-helper.ts"
import useDelayedFunction from "@/composables/input/delayed-function.ts"

const props = withDefaults(
  defineProps<{
    allowedRoles?: RoleTypes[] | null
  }>(),
  {
    allowedRoles: null
  }
)

const modelValue = defineModel<KnownBusinessEntityContact | null>()

const hasOptionsOpen = ref(false)

// Do not inherit all attrs because v-model will be inherited by InputCombobox, which we don't want
defineOptions({
  inheritAttrs: false
})

const attrs = useAttrs()

const inheritedAttrs = computed((): Record<string, unknown> => {
  const _inheritedAttrs: Record<string, unknown> = { ...attrs }
  delete _inheritedAttrs["v-model"] // We want all attrs to be inherited, except v-model
  delete _inheritedAttrs["v-on:input"] // We want all attrs to be inherited, except v-model
  return _inheritedAttrs
})

const selectedKnownBusinessEntityContact: Ref<KnownBusinessEntityContact | null | undefined> =
  ref(null)

watch(
  modelValue,
  (val) => {
    selectedKnownBusinessEntityContact.value = val
  },
  { immediate: true }
)

const emit = defineEmits(["isCreatingNewItem", "itemCreated", "update:modelValue"])

const knownBusinessEntitiesContactsStore = useKnownBusinessEntitiesContactsStore()
const { activeKnownBusinessEntitiesContacts } = storeToRefs(knownBusinessEntitiesContactsStore)

const allowedItems = computed(() => {
  if (!props.allowedRoles) {
    return activeKnownBusinessEntitiesContacts.value
  }
  return activeKnownBusinessEntitiesContacts.value.filter((kbec) => {
    return kbec.roles.some((role) => props.allowedRoles!.includes(role.type))
  })
})

const filteredAllowedItems = ref<KnownBusinessEntityContact[]>([])

const comboboxText = ref("")
function filterItems() {
  filteredAllowedItems.value = fuzzySearchCollection(
    allowedItems.value,
    [
      { name: "person.firstName" },
      { name: "person.lastName" },
      { name: "knownBusinessEntity.name" }
    ],
    comboboxText.value
  )
}

const delayedFilterItems = useDelayedFunction(filterItems, 100)
watch(comboboxText, () => delayedFilterItems.schedule())

// Display the known business entity only if changes were made or if it was just created.
const displayKnownBusinessEntity = ref(false)

function onItemSaved(kbecOrPerson: KnownBusinessEntityContact | Person) {
  if (kbecOrPerson instanceof KnownBusinessEntityContact) {
    if (!allowedItems.value.map((kbec) => kbec.id).includes(kbecOrPerson.id)) {
      // KBEC was updated but does not meet the role requirements. Clear the combobox.
      selectedKnownBusinessEntityContact.value = null
      return
    }

    displayKnownBusinessEntity.value = true
    selectedKnownBusinessEntityContact.value = kbecOrPerson
    emit("itemCreated", kbecOrPerson)
  }
}

function onItemCreated(personOrKBEC: Person | KnownBusinessEntityContact) {
  displayKnownBusinessEntity.value = true
  if (personOrKBEC instanceof KnownBusinessEntityContact) {
    if (!allowedItems.value.map((kbec) => kbec.id).includes(personOrKBEC.id)) {
      // KBEC was updated but does not meet the role requirements. Clear the combobox.
      selectedKnownBusinessEntityContact.value = null
      return
    }

    if (selectedKnownBusinessEntityContact.value?.id === personOrKBEC.id) {
      // Important to use Object.assign to make sure identical objects in different comboboxes are updated to the same
      // value.
      Object.assign(selectedKnownBusinessEntityContact.value!, personOrKBEC)
    } else {
      selectedKnownBusinessEntityContact.value = personOrKBEC
      emit("update:modelValue", personOrKBEC)
    }
  } else if (selectedKnownBusinessEntityContact.value) {
    Object.assign(selectedKnownBusinessEntityContact.value.person, personOrKBEC)
  }
  emit("itemCreated", personOrKBEC)
}

function getSelectedItemDisplay() {
  return displayKnownBusinessEntity.value
    ? selectedKnownBusinessEntityContact.value?.getDisplayText()
    : selectedKnownBusinessEntityContact.value?.person?.fullName
}

const combobox = ref()
defineExpose({
  focus: () => combobox.value?.focus()
})
</script>

<style scoped></style>
